import React from 'react';

import type { ClassValue } from 'tailwind-variants';
import { tv } from 'tailwind-variants';

export type TypographyProps = {
  children?: React.ReactNode;
  className?: ClassValue;
  as?: keyof JSX.IntrinsicElements;
  variant?:
    | 'h0'
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'body1'
    | 'body2'
    | 'body3'
    | 'body4'
    | 'body5'
    | 'body6';
  position?: 'left' | 'center' | 'right';
};

const componentMap = {
  h0: 'h1',
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',

  body1: 'p',
  body2: 'p',
  body3: 'p',
  body4: 'p',
  body5: 'p',
  body6: 'p',
};

const typographyVariants = tv({
  base: 'text-gray-900',
  variants: {
    variant: {
      h0: 'text-[4rem] leading-[4rem] font-semibold',
      h1: 'text-[2rem] leading-10 font-semibold',
      h2: 'text-2xl leading-8	font-semibold',
      h3: 'text-xl leading-5 font-semibold',
      h4: 'text-base font-semibold',
      h5: 'text-sm leading-5 font-semibold',
      h6: 'text-xs leading-5 font-bold',

      body1: 'text-xl leading-6 font-semibold',
      body2: 'text-base font-medium',
      body3: 'text-base font-normal',
      body4: 'text-sm	leading-5 font-normal',
      body5: 'text-xs leading-5 font-normal',
      body6: 'text-xs leading-[1.125rem] font-normal',
    },
    position: {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
    },
  },
});

export const Typography = ({
  children,
  variant = 'body3',
  position = 'left',
  as,
  className,
}: TypographyProps) => {
  const Tag = as ?? (componentMap[variant] as keyof JSX.IntrinsicElements);

  return (
    <Tag className={typographyVariants({ variant, position, className })}>
      {typeof children === 'string'
        ? children.split('\n').map((line, index) => (
            <React.Fragment key={index}>
              {index > 0 && <br />}
              {line}
            </React.Fragment>
          ))
        : children}
    </Tag>
  );
};
